'use client'

import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { ImageIcon, StarIcon } from 'lucide-react'
import { PhotoProvider, PhotoView } from 'react-photo-view'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { getResizedPictureUrl, tOptional } from '@/lib/utils'
import { RelatedFileType } from '@/types/file'

type PropsType = {
  pictures?: RelatedFileType[]
  title?: string
}

const ContentDetailPictures = ({ pictures, title }: PropsType) => {
  const t = useTranslations()

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ImageIcon className="h-5 w-5" />
          {title ? tOptional(t, title) : tOptional(t, 'pictures')}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {pictures && pictures.length > 0 ? (
          <div className="xs:grid-cols-12 grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-6 lg:grid-cols-8">
            <PhotoProvider maskOpacity={0.5}>
              {pictures.map((picture, index) => {
                const isMain = picture.isMain ?? picture.file?.isMain ?? false
                return (
                  <div key={picture.id} className="bg-checkerboard relative w-32 overflow-hidden rounded-md border">
                    {isMain && (
                      <div
                        className="bg-primary/90 text-primary-foreground pointer-events-none absolute top-1.5 right-1.5 z-10 flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] shadow"
                        title={t('main_picture')}>
                        <StarIcon className="h-3 w-3" />
                        <span className="sr-only">{t('main_picture')}</span>
                      </div>
                    )}
                    <PhotoView src={getResizedPictureUrl(picture.file.path, 800, 600)}>
                      <Image
                        src={getResizedPictureUrl(picture.file.path, 400, 300)}
                        alt={picture.file.name || `Picture ${index + 1}`}
                        width={240}
                        height={240}
                        className="max-h-100 w-auto cursor-pointer rounded-md border object-contain hover:border-gray-500"
                      />
                    </PhotoView>
                  </div>
                )
              })}
            </PhotoProvider>
          </div>
        ) : (
          <p className="text-muted-foreground">{t('no_pictures')}</p>
        )}
      </CardContent>
    </Card>
  )
}

export default ContentDetailPictures
